import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Loader2, ChefHat, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { BRAND } from "@/constants/brand";
import { RecipeCard } from "@/components/RecipeCard";
import { CookingMode } from "@/components/CookingMode";
import { addGroceryItems } from "@/lib/grocery";

export default function SharedRecipe() {
  const { shareId } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [status, setStatus] = useState("loading");
  const [cooking, setCooking] = useState(false);

  useEffect(() => {
    api
      .get(`/recipes/shared/${shareId}`)
      .then(({ data }) => {
        setRecipe(data.recipe);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  }, [shareId]);

  return (
    <div className="min-h-screen bg-cream px-4 py-10">
      <div className="mx-auto max-w-md">
        <Link to="/" className="mb-6 flex items-center justify-center gap-2" data-testid="shared-brand-link">
          <img src={BRAND.logo} alt="Aunt Lorettas Kitchen Chat" className="h-14 w-14 rounded-full object-contain" />
          <div className="text-left">
            <p className="font-serif text-lg font-bold leading-none text-espresso">Aunt Lorettas Kitchen Chat</p>
            <p className="text-xs tracking-wide text-loretta-red">{BRAND.tagline}</p>
          </div>
        </Link>

        {status === "loading" && (
          <div className="mt-16 text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-loretta-red" />
          </div>
        )}

        {status === "error" && (
          <div className="mt-10 rounded-3xl border border-espresso/10 bg-white p-8 text-center">
            <ChefHat className="mx-auto mb-3 h-10 w-10 text-espresso/30" />
            <h1 className="font-serif text-2xl font-bold text-espresso">Recipe not found</h1>
            <p className="mt-2 text-espressolt">This shared recipe may have expired, sugar.</p>
          </div>
        )}

        {status === "ok" && recipe && (
          <>
            <p className="mb-3 text-center text-sm text-espressolt">A recipe shared from Aunt Lorettas Kitchen Chat</p>
            <div className="flex justify-center">
              <RecipeCard
                recipe={recipe}
                onStartCooking={() => setCooking(true)}
                onAddToList={(r) => {
                  const n = addGroceryItems(r.ingredients || []);
                  toast.success(n > 0 ? `${n} ingredients added to your grocery list!` : "Already on your list, sugar.");
                }}
              />
            </div>
            <Link
              to="/"
              data-testid="shared-cta"
              className="mt-6 flex items-center justify-center gap-2 rounded-full bg-loretta-olive py-3.5 font-semibold text-cream transition-colors hover:bg-loretta-olivehover"
            >
              Chat with Aunt Loretta <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
      </div>

      <AnimatePresence>
        {cooking && recipe && <CookingMode recipe={recipe} onClose={() => setCooking(false)} />}
      </AnimatePresence>
    </div>
  );
}
